import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { api, CATEGORIES, GENDERS, euro } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const Chip = ({ active, onClick, children, testid, small }) => (
  <button onClick={onClick} data-testid={testid}
    className={`${small ? "min-w-[44px] px-2 h-9 text-sm" : "px-4 h-9 text-xs uppercase tracking-wide"} rounded-full font-semibold border transition-colors ${active ? "bg-brand text-white border-brand" : "bg-white border-slate-200 hover:border-brand"}`}>
    {children}
  </button>
);

const SizeFilter = ({ outlet, category, value, onChange }) => {
  const [sizes, setSizes] = useState([]);
  const [find, setFind] = useState("");
  useEffect(() => {
    api.get("/products/sizes", { params: { outlet, category } }).then((r) => setSizes(r.data));
  }, [outlet, category]);
  const norm = (s) => s.replace(",", ".").toLowerCase();
  const shown = find ? sizes.filter((s) => norm(s).startsWith(norm(find))) : sizes;
  return (
    <div data-testid="filter-size">
      <p className="filter-title">Talla / número</p>
      <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Escribe tu talla (ej. 42)" className="field !h-9 mb-3" data-testid="filter-size-input" />
      <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-1">
        <Chip small active={!value} onClick={() => onChange("")} testid="filter-size-all">Todas</Chip>
        {shown.map((s) => <Chip small key={s} active={value === s} onClick={() => onChange(s)} testid={`filter-size-${s}`}>{s}</Chip>)}
        {find && shown.length === 0 && <p className="text-xs text-slate-500">No hay stock en esa talla.</p>}
      </div>
    </div>
  );
};

const OutletHero = () => (
  <section className="bg-verde text-white grain" data-testid="outlet-hero">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-14">
      <p className="eyebrow eyebrow-light !text-white/80">Sobrino Sport</p>
      <h1 className="mt-2 font-heading text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-none">Outlet</h1>
      <p className="mt-4 max-w-lg text-white/90">Últimas unidades de marcas top a precios rebajados. Cuando se acaban, no vuelven.</p>
    </div>
  </section>
);

export default function Catalog({ outlet: outletProp = false }) {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState(null);
  const [maxPrice, setMaxPrice] = useState(200);
  const outlet = outletProp;
  const category = params.get("category") || "";
  const gender = params.get("gender") || "";
  const size = params.get("size") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "new";
  const inStock = params.get("in_stock") === "1";
  const [search, setSearch] = useState(q);

  const set = (k, v) => {
    const next = new URLSearchParams(params);
    v ? next.set(k, v) : next.delete(k);
    if (k === "category") next.delete("size");
    setParams(next);
  };

  useEffect(() => { setSearch(q); }, [q]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/products", { params: { category, gender, size, q, sort, outlet, in_stock: inStock, max_price: maxPrice < 200 ? maxPrice : 0 } })
        .then((r) => setProducts(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [category, gender, size, q, sort, inStock, outlet, maxPrice]);

  const catLabel = CATEGORIES.find((c) => c.id === category)?.label;
  const title = catLabel || "Toda la tienda";

  return (
    <div data-testid={outlet ? "outlet-page" : "catalog-page"}>
      {outlet && <OutletHero />}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-14">
        {!outlet && <><p className="eyebrow">Catálogo</p><h1 className="mt-2 font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight">{title}</h1></>}
        {outlet && catLabel && <h2 className="font-heading text-3xl font-black uppercase tracking-tight">{catLabel}</h2>}
        <div className="mt-10 grid lg:grid-cols-[240px_1fr] gap-12">
          <aside className="space-y-8" data-testid="catalog-filters">
            <div>
              <p className="filter-title">Categoría</p>
              <div className="flex flex-wrap gap-2">
                <Chip active={!category} onClick={() => set("category", "")} testid="filter-cat-all">Todo</Chip>
                {CATEGORIES.map((c) => <Chip key={c.id} active={category === c.id} onClick={() => set("category", c.id)} testid={`filter-cat-${c.id}`}>{c.label}</Chip>)}
              </div>
            </div>
            <SizeFilter key={category} outlet={outlet} category={category} value={size} onChange={(v) => set("size", v)} />
            <div>
              <p className="filter-title">Género</p>
              <div className="flex flex-wrap gap-2">
                <Chip active={!gender} onClick={() => set("gender", "")} testid="filter-gender-all">Todos</Chip>
                {GENDERS.filter((g) => g.id !== "unisex").map((g) => <Chip key={g.id} active={gender === g.id} onClick={() => set("gender", g.id)} testid={`filter-gender-${g.id}`}>{g.label}</Chip>)}
              </div>
            </div>
            <div>
              <p className="filter-title">Precio máximo: <span className="text-slate-900">{maxPrice >= 200 ? "Sin límite" : euro(maxPrice)}</span></p>
              <Slider value={[maxPrice]} min={10} max={200} step={5} onValueChange={(v) => setMaxPrice(v[0])} data-testid="filter-price-slider" />
            </div>
            <label className="flex items-center justify-between text-sm font-semibold">
              Solo con stock
              <Switch checked={inStock} onCheckedChange={(v) => set("in_stock", v ? "1" : "")} data-testid="filter-instock-switch" />
            </label>
          </aside>
          <section>
            <div className="flex flex-wrap items-center gap-3 justify-between mb-6">
              <form onSubmit={(e) => { e.preventDefault(); set("q", search.trim()); }} className="flex items-center bg-white border border-slate-200 rounded-full px-4 h-10 w-full sm:w-72 focus-within:border-brand">
                <Search className="w-4 h-4 text-slate-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={outlet ? "Buscar en el outlet" : "Buscar en la tienda"} className="bg-transparent outline-none text-sm ml-2 w-full" data-testid="catalog-search-input" />
                {q && <button type="button" onClick={() => set("q", "")} data-testid="clear-search"><X className="w-4 h-4 text-slate-400" /></button>}
              </form>
              <div className="flex items-center gap-3">
                <p className="text-sm text-slate-500" data-testid="catalog-count">{products ? `${products.length} productos` : "Cargando…"}</p>
                <select value={sort} onChange={(e) => set("sort", e.target.value)} className="h-10 border border-slate-200 rounded-full px-4 text-sm bg-white" data-testid="catalog-sort-select">
                  <option value="new">Novedades</option>
                  <option value="price_asc">Precio: menor a mayor</option>
                  <option value="price_desc">Precio: mayor a menor</option>
                </select>
              </div>
            </div>
            {size && <p className="mb-6 text-sm">Mostrando artículos con stock en la talla <b data-testid="active-size">{size}</b></p>}
            {products?.length === 0 && (
              <div className="py-20 text-center text-slate-500" data-testid="catalog-empty">
                <p>No hay productos con estos filtros.</p>
                <p className="mt-2 text-sm">¿Buscas algo concreto? Puede que lo tengamos en la tienda.</p>
                <a href="https://wa.me/34661666916" target="_blank" rel="noreferrer" className="mt-4 inline-flex btn-primary btn-verde" data-testid="catalog-empty-whatsapp">Pregúntanos por WhatsApp</a>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-10" data-testid="catalog-grid">
              {products?.map((p, i) => <ProductCard key={p.id} product={p} index={i} size={size} />)}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
